// src/components/CookieConsent.js
//
// PDPA-oriented cookie consent card.
// - Nothing tracked by default until the user actively chooses (opt-in).
// - Choice remembered in localStorage; CONSENT_VERSION lets you force
//   re-prompting everyone later if you add new cookie categories.
// - Links to your Privacy Policy page (App.js already has a route at /privacy).

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { grantAnalyticsConsent, revokeAnalyticsConsent } from "../utils/analytics";
import "./CookieConsent.css";

const STORAGE_KEY = "kmc_cookie_consent";
const CONSENT_VERSION = "1";
const COOKIE_POLICY_PATH = "/cookie-policy";

function ShieldCheckIcon() {
  return (
    <svg
      className="cookie-consent__icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 3l7 3v5c0 4.4-2.9 8.3-7 9.5-4.1-1.2-7-5.1-7-9.5V6l7-3z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  );
}

export default function CookieConsent() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let stored = null;
    try {
      stored = JSON.parse(localStorage.getItem(STORAGE_KEY));
    } catch {
      stored = null;
    }

    if (stored && stored.version === CONSENT_VERSION) {
      if (stored.analytics) grantAnalyticsConsent();
      else revokeAnalyticsConsent();
      setVisible(false);
    } else {
      setVisible(true);
    }
  }, []);

  const save = (analyticsGranted) => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ version: CONSENT_VERSION, analytics: analyticsGranted, ts: Date.now() })
    );
    if (analyticsGranted) grantAnalyticsConsent();
    else revokeAnalyticsConsent();
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="cookie-consent">
      <div
        className="cookie-consent__card"
        role="dialog"
        aria-live="polite"
        aria-label={t("cookieConsent.ariaLabel")}
      >
        <div className="cookie-consent__body">
          <div className="cookie-consent__header">
            <ShieldCheckIcon />
            <p className="cookie-consent__title">{t("cookieConsent.title")}</p>
          </div>

          <p className="cookie-consent__message">
            {t("cookieConsent.message")}{" "}
            <Link to={COOKIE_POLICY_PATH} className="cookie-consent__link">
              {t("cookieConsent.policyLink")}
            </Link>
          </p>

          <div className="cookie-consent__actions">
            <button
              className="cookie-consent__btn cookie-consent__btn--secondary"
              onClick={() => save(false)}
            >
              {t("cookieConsent.necessaryOnly")}
            </button>
            <button
              className="cookie-consent__btn cookie-consent__btn--primary"
              onClick={() => save(true)}
            >
              {t("cookieConsent.acceptAll")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}